/**
 * ConnectionErrorBanner - Displays connection validation errors with recovery actions
 *
 * Shows error/warning messages for connection issues with appropriate
 * action buttons (retry, reauthorize, select connection).
 */

import { useCallback } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Button,
  Text,
  mergeClasses,
} from '@coreai/fluentui-react';
import {
  ErrorCircleFilled,
  WarningFilled,
  PlugConnectedRegular,
  ChevronRightRegular,
  Database20Regular,
} from '@fluentui/react-icons';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { isHostedAgentConnectionReference } from '@/utils/connections';
import { useNodeConnectionStatus } from '@/hooks/useNodeConnectionStatus';
import { ConnectionValidationStatus } from '@/types/connectionValidation';
import { MANAGED_MODEL_REFERENCE_KEY, MANAGED_MODEL_DISPLAY_NAME } from '@/utils/connections/managedModelReference';

const useStyles = makeStyles({
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
  },
  bannerError: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPaletteRedBorder1),
  },
  bannerWarning: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPaletteYellowBorder1),
  },
  bannerConnected: {
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  bannerMock: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorPaletteBlueBorderActive),
  },
  iconError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
  },
  iconWarning: {
    color: tokens.colorPaletteYellowForeground1,
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
  },
  iconConnected: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
  },
  iconMock: {
    color: tokens.colorPaletteBlueForeground2,
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  labelText: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  title: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  connectionName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  message: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  manageButton: {
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightMedium,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
  },
});

interface ConnectionErrorBannerProps {
  /** Node ID (defaults to selected node) */
  nodeId?: string;
  /** Operation type (defaults to selected node's type) */
  operationType?: string;
  /** Connection reference key (for Agent operations, the modelConfigurations.model1.referenceName) */
  connectionReferenceKey?: string | undefined;
  /** Whether to show a compact version */
  compact?: boolean;
  /** Callback to navigate to connection section/tab (used instead of opening popup) */
  onNavigateToConnection?: () => void;
}

/**
 * ConnectionErrorBanner - Shows connection errors with recovery actions
 */
export const ConnectionErrorBanner = ({
  nodeId: propNodeId,
  operationType: propOperationType,
  connectionReferenceKey,
  compact = false,
  onNavigateToConnection,
}: ConnectionErrorBannerProps) => {
  const styles = useStyles();

  // Get node from store if not provided
  const selectedNode = useNodeEditorStore((state) => state.node);
  const parameterValues = useNodeEditorStore((state) => state.parameterValues);
  const openConnectionPicker = useNodeEditorStore((state) => state.openConnectionPicker);
  const connectionReferences = useConnectionStore((state) => state.references);

  const nodeId = propNodeId ?? selectedNode?.id ?? '';
  const operationType = propOperationType ?? selectedNode?.nodeData?.type ?? '';

  // Get the connection reference key from parameters if not provided
  // For Agent operations, it's stored in _modelConnection_model1
  // For other operations, it's stored in _connection
  const isAgentOperation = operationType === 'Agent' || operationType === 'AgentLoop';

  // Get connection key from parameterValues or directly from nodeData.inputs
  let derivedConnectionKey = connectionReferenceKey;
  if (!derivedConnectionKey && parameterValues) {
    if (isAgentOperation) {
      derivedConnectionKey = parameterValues['_modelConnection_model1'] as string | undefined;
      // Fallback: check node data directly if not in parameterValues
      if (!derivedConnectionKey && selectedNode?.nodeData) {
        const inputs = (selectedNode.nodeData as Record<string, unknown>).inputs as Record<string, unknown> | undefined;
        const modelConfigs = inputs?.modelConfigurations as Record<string, Record<string, unknown>> | undefined;
        derivedConnectionKey = modelConfigs?.model1?.referenceName as string | undefined;
      }
      // Hosted agents: during editing the selection can live in the transient
      // hostedAgentName before _modelConnection_model1 is written. It holds the same
      // reference key, so fall back to it (matches how the Config/Connection tabs
      // resolve the selection) — otherwise the banner shows "Not configured" despite
      // an active selection. These params only exist for hosted agents, so this is a
      // no-op for other agent types.
      if (!derivedConnectionKey) {
        derivedConnectionKey = (parameterValues['hostedAgentName']
          ?? parameterValues['parameters.hostedAgentName']) as string | undefined;
      }
    } else {
      derivedConnectionKey = parameterValues['_connection'] as string | undefined;
    }
  }

  // Detect hosted agent nodes (agentModelType = FoundryHostedAgent) so we can render
  // hosted-agent-specific banners. Their connection IS tracked in connectionStore —
  // extractConnectionReferences maps hostedAgentConnections → api.id `/hostedAgentConnections/…`
  // — so validity is resolved by useNodeConnectionStatus below (not a referenceName-only check).
  const isHostedAgentConnection = isAgentOperation && parameterValues &&
    (parameterValues['agentModelType'] === 'FoundryHostedAgent' ||
     parameterValues['parameters.agentModelType'] === 'FoundryHostedAgent');

  // Use the same hook as AgentNode and ActionNode for connection validation
  const { validationResult, hasConnection, connectionName, referenceKey: resolvedReferenceKey } = useNodeConnectionStatus({
    nodeId,
    operationType,
    connectionReferenceKey: derivedConnectionKey,
  });

  // A stale cross-type binding is a reference where the agent variant and the bound connection
  // type disagree. Hosted-agent connections (`…/hostedAgentConnections/…`) are only valid for the
  // Foundry Hosted Agent variant; every other agent connection is only valid for the non-hosted
  // variants. `useNodeConnectionStatus` resolves the effective key from the passed key OR the
  // node→reference mapping, so classify the RESOLVED key (nulling the passed key alone wouldn't
  // help — the hook falls back to the mapping). Only agent connections are scoped this way.
  const boundReferenceIsHostedConnection =
    isHostedAgentConnectionReference(connectionReferences, resolvedReferenceKey);
  const isCrossTypeBinding =
    isAgentOperation &&
    (isHostedAgentConnection
      // Reverse (#2552 mirror): a hosted agent left bound to a NON-hosted connection (e.g. after
      // switching an agent's variant to Hosted). Gate on hasConnection so we only suppress an
      // actually-resolved wrong-type connection, not a hosted agent with nothing selected yet.
      ? hasConnection && !boundReferenceIsHostedConnection
      // Original (#2552): a non-hosted agent bound to a hosted-agent connection.
      : !!resolvedReferenceKey && boundReferenceIsHostedConnection);

  // Handle select connection action
  // If onNavigateToConnection is provided, use it to switch tabs instead of opening popup
  const handleSelectConnection = useCallback(() => {
    if (onNavigateToConnection) {
      onNavigateToConnection();
    } else {
      openConnectionPicker();
    }
  }, [onNavigateToConnection, openConnectionPicker]);

  // Hide the banner entirely for a stale cross-type binding in EITHER direction so a connection
  // that doesn't match the agent variant is never surfaced here — the correct connection is
  // chosen in the Connection tab for non-hosted agents, or via HostedAgentConnectionTab (now
  // rendered inside the Parameters tab, issue #2732) for hosted agents. Placed after all hooks
  // to keep hook order stable.
  if (isCrossTypeBinding) {
    return null;
  }

  // MockActive: show informational blue banner
  if (validationResult?.status === ConnectionValidationStatus.MockActive) {
    return (
      <div
        className={mergeClasses(styles.banner, styles.bannerMock)}
        role="status"
      >
        <Database20Regular className={styles.iconMock} aria-hidden="true" />
        <div className={styles.content}>
          <Text className={styles.title}>Using Test Data</Text>
          {!compact && (
            <Text className={styles.message}>
              This action will use test data during test runs. Configure a real connection to use live data.
            </Text>
          )}
        </div>
        <Button
          appearance="transparent"
          size="small"
          onClick={handleSelectConnection}
          className={styles.manageButton}
        >
          Manage <ChevronRightRegular />
        </Button>
      </div>
    );
  }

  // If there's a valid connection, show a simple "manage" link instead of error banner
  if (validationResult?.isValid || hasConnection) {
    return (
      <div
        className={mergeClasses(styles.banner, styles.bannerConnected)}
        role="status"
      >
        <PlugConnectedRegular className={styles.iconConnected} aria-hidden="true" />
        <div className={styles.content}>
          <Text className={styles.labelText}>CONNECTION</Text>
          <Text className={styles.connectionName}>
            {(() => {
              const raw = connectionName ?? derivedConnectionKey;
              if (raw === MANAGED_MODEL_REFERENCE_KEY) return MANAGED_MODEL_DISPLAY_NAME;
              return raw ?? 'Connected';
            })()}
          </Text>
        </div>
        <Button
          appearance="transparent"
          size="small"
          onClick={handleSelectConnection}
          className={styles.manageButton}
        >
          Manage <ChevronRightRegular />
        </Button>
      </div>
    );
  }

  // Don't render if no validation needed (e.g., operation doesn't require connection)
  // For hosted agents without a connection, show a missing banner
  if (!validationResult && !isHostedAgentConnection) {
    return null;
  }

  // For hosted agents with no agent selected, show a "Not configured" banner
  if (isHostedAgentConnection && !derivedConnectionKey) {
    return (
      <div
        className={mergeClasses(styles.banner, styles.bannerError)}
        role="alert"
      >
        <ErrorCircleFilled className={styles.iconError} aria-hidden="true" />
        <div className={styles.content}>
          <Text className={styles.labelText}>AGENT</Text>
          <Text className={styles.title}>Not configured</Text>
          {!compact && (
            <Text className={styles.message}>Select a hosted agent to continue</Text>
          )}
        </div>
        <Button
          appearance="transparent"
          size="small"
          onClick={handleSelectConnection}
          className={styles.manageButton}
        >
          Configure <ChevronRightRegular />
        </Button>
      </div>
    );
  }

  if (!validationResult) {
    return null;
  }

  const isWarning = validationResult.status === ConnectionValidationStatus.Warning;
  const error = validationResult.error;

  // Determine title based on status
  const title = isWarning
    ? 'Connection Warning'
    : validationResult.status === ConnectionValidationStatus.Missing
      ? 'Not configured'
      : 'Connection Error';

  return (
    <div
      className={mergeClasses(
        styles.banner,
        isWarning ? styles.bannerWarning : styles.bannerError
      )}
      role="alert"
    >
      {isWarning ? (
        <WarningFilled className={styles.iconWarning} aria-hidden="true" />
      ) : (
        <ErrorCircleFilled className={styles.iconError} aria-hidden="true" />
      )}

      <div className={styles.content}>
        <Text className={styles.labelText}>CONNECTION</Text>
        <Text className={styles.title}>{title}</Text>
        {!compact && error?.message && (
          <Text className={styles.message}>{error.message}</Text>
        )}
      </div>

      <Button
        appearance="transparent"
        size="small"
        onClick={handleSelectConnection}
        className={styles.manageButton}
      >
        Manage <ChevronRightRegular />
      </Button>
    </div>
  );
};

ConnectionErrorBanner.displayName = 'ConnectionErrorBanner';
